import React, { useState, useEffect, useRef } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { CheckSquare, Plus, X, AlertTriangle, Sparkles } from 'lucide-react';

const C4_Assumptions = () => {
  const { dataC, dataA, updateDataRaw, nextStep, previousStep } = useWizard();
  const hasAutoPopulated = useRef(false);
  const [assumptions, setAssumptions] = useState(dataC?.assumptions || {
    ontological: [],
    epistemological: [],
    axiological: [],
    methodological: []
  });
  const [wizardADerived, setWizardADerived] = useState({
    ontological: [],
    epistemological: [],
    axiological: [],
    methodological: []
  });
  const [customAssumption, setCustomAssumption] = useState({ category: 'ontological', text: '' });
  const [showDerivedWarning, setShowDerivedWarning] = useState(null);

  const predefinedAssumptions = {
    ontological: [
      'Reality exists independently of human perception',
      'Multiple realities exist based on individual experiences',
      'Social reality is constructed through interactions',
      'Reality is shaped by power structures and historical context'
    ],
    epistemological: [
      'Knowledge can be objectively measured and verified',
      'Knowledge is subjective and context-dependent',
      'Knowledge emerges from social processes',
      'The researcher and researched co-create knowledge'
    ],
    axiological: [
      'Research should be value-free and objective',
      'Values are inherent and should be acknowledged',
      'Research should actively promote social change',
      'Participant values are central to the research'
    ],
    methodological: [
      'Phenomena can be reduced to measurable variables',
      'Rich descriptions capture complex phenomena',
      'Mixed methods provide comprehensive understanding',
      'Action and reflection drive knowledge creation'
    ]
  };

  const dimensionToAssumption = {
    ontology: {
      realist: predefinedAssumptions.ontological[0],
      relativist: predefinedAssumptions.ontological[1],
      pragmatic: predefinedAssumptions.ontological[2]
    },
    epistemology: {
      objectivist: predefinedAssumptions.epistemological[0],
      subjectivist: predefinedAssumptions.epistemological[1],
      critical: predefinedAssumptions.epistemological[2],
      practical: predefinedAssumptions.epistemological[3]
    },
    axiology: {
      value_free: predefinedAssumptions.axiological[0],
      value_laden: predefinedAssumptions.axiological[1],
      value_driven: predefinedAssumptions.axiological[2],
      pragmatic_values: predefinedAssumptions.axiological[3]
    }
  };

  const methodToAssumption = {
    mono_quantitative: predefinedAssumptions.methodological[0],
    multi_quantitative: predefinedAssumptions.methodological[0],
    mono_qualitative: predefinedAssumptions.methodological[1],
    multi_qualitative: predefinedAssumptions.methodological[1],
    mixed: predefinedAssumptions.methodological[2]
  };

  useEffect(() => {
    if (dataC?.assumptions && Object.values(dataC.assumptions).some(arr => arr.length > 0)) {
      setAssumptions(dataC.assumptions);
      hasAutoPopulated.current = true;
    }
  }, [dataC?.assumptions]);

  useEffect(() => {
    if (hasAutoPopulated.current) return;
    
    const derived = {
      ontological: [],
      epistemological: [],
      axiological: [],
      methodological: []
    };
    const autoSelected = {
      ontological: [],
      epistemological: [],
      axiological: [],
      methodological: []
    };

    if (dataA?.philosophicalDimensions) {
      const dims = dataA.philosophicalDimensions;
      
      if (dims.ontology && dimensionToAssumption.ontology[dims.ontology.toLowerCase()]) {
        const assumption = dimensionToAssumption.ontology[dims.ontology.toLowerCase()];
        derived.ontological.push(assumption);
        autoSelected.ontological.push(assumption);
      }
      
      if (dims.epistemology && dimensionToAssumption.epistemology[dims.epistemology.toLowerCase()]) {
        const assumption = dimensionToAssumption.epistemology[dims.epistemology.toLowerCase()];
        derived.epistemological.push(assumption);
        autoSelected.epistemological.push(assumption);
      }
      
      if (dims.axiology && dimensionToAssumption.axiology[dims.axiology.toLowerCase()]) {
        const assumption = dimensionToAssumption.axiology[dims.axiology.toLowerCase()];
        derived.axiological.push(assumption);
        autoSelected.axiological.push(assumption);
      }
    }

    if (dataA?.methodologicalChoice) {
      const method = dataA.methodologicalChoice.toLowerCase();
      if (methodToAssumption[method]) {
        derived.methodological.push(methodToAssumption[method]);
        autoSelected.methodological.push(methodToAssumption[method]);
      }
    }

    if (Object.values(derived).some(arr => arr.length > 0)) {
      setWizardADerived(derived);
      setAssumptions(autoSelected);
      updateDataRaw({ 'wizardC.assumptions': autoSelected });
      hasAutoPopulated.current = true;
    }
  }, [dataA?.philosophicalDimensions, dataA?.methodologicalChoice, updateDataRaw]);

  const getRecommendedAssumptions = () => {
    const paradigm = dataA?.philosophy;
    const recommendations = {
      positivism: {
        ontological: [predefinedAssumptions.ontological[0]],
        epistemological: [predefinedAssumptions.epistemological[0]],
        axiological: [predefinedAssumptions.axiological[0]],
        methodological: [predefinedAssumptions.methodological[0]]
      },
      interpretivism: {
        ontological: [predefinedAssumptions.ontological[1]],
        epistemological: [predefinedAssumptions.epistemological[1]],
        axiological: [predefinedAssumptions.axiological[1]],
        methodological: [predefinedAssumptions.methodological[1]]
      },
      constructivism: {
        ontological: [predefinedAssumptions.ontological[2]],
        epistemological: [predefinedAssumptions.epistemological[2]],
        axiological: [predefinedAssumptions.axiological[1]],
        methodological: [predefinedAssumptions.methodological[1]]
      },
      pragmatism: {
        ontological: [predefinedAssumptions.ontological[0], predefinedAssumptions.ontological[1]],
        epistemological: [predefinedAssumptions.epistemological[2]],
        axiological: [predefinedAssumptions.axiological[1]],
        methodological: [predefinedAssumptions.methodological[2]]
      }
    };
    return recommendations[paradigm] || null;
  };

  const isDerivedFromWizardA = (category, assumption) => {
    return wizardADerived[category]?.includes(assumption);
  };

  const toggleAssumption = (category, assumption) => {
    const isDerived = isDerivedFromWizardA(category, assumption);
    const isCurrentlySelected = (assumptions[category] || []).includes(assumption);
    
    if (isDerived && isCurrentlySelected) {
      setShowDerivedWarning({ category, assumption });
      return;
    }
    
    const current = assumptions[category] || [];
    const updated = current.includes(assumption)
      ? current.filter(a => a !== assumption)
      : [...current, assumption];
    
    const newAssumptions = { ...assumptions, [category]: updated };
    setAssumptions(newAssumptions);
    updateDataRaw({ 'wizardC.assumptions': newAssumptions });
  };

  const confirmRemoveDerived = () => {
    if (!showDerivedWarning) return;
    const { category, assumption } = showDerivedWarning;
    const current = assumptions[category] || [];
    const updated = current.filter(a => a !== assumption);
    const newAssumptions = { ...assumptions, [category]: updated };
    setAssumptions(newAssumptions);
    updateDataRaw({ 'wizardC.assumptions': newAssumptions });
    setShowDerivedWarning(null);
  };

  const addCustomAssumption = () => {
    if (customAssumption.text.trim()) {
      const current = assumptions[customAssumption.category] || [];
      const newAssumptions = {
        ...assumptions,
        [customAssumption.category]: [...current, customAssumption.text.trim()]
      };
      setAssumptions(newAssumptions);
      updateDataRaw({ 'wizardC.assumptions': newAssumptions });
      setCustomAssumption({ ...customAssumption, text: '' });
    }
  };

  const removeAssumption = (category, assumption) => {
    const current = assumptions[category] || [];
    const newAssumptions = {
      ...assumptions,
      [category]: current.filter(a => a !== assumption)
    };
    setAssumptions(newAssumptions);
    updateDataRaw({ 'wizardC.assumptions': newAssumptions });
  };

  const recommendations = getRecommendedAssumptions();
  const totalSelected = Object.values(assumptions).flat().length;

  const categoryLabels = {
    ontological: { label: 'Ontological', desc: 'Nature of reality', color: '#3b82f6' },
    epistemological: { label: 'Epistemological', desc: 'Nature of knowledge', color: '#22c55e' },
    axiological: { label: 'Axiological', desc: 'Role of values', color: '#f59e0b' },
    methodological: { label: 'Methodological', desc: 'Research approach', color: '#8b5cf6' }
  };

  const handleContinue = () => {
    updateDataRaw({ 'wizardC.assumptions': assumptions });
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon assumptions-icon">
          <CheckSquare size={32} />
        </div>
        <div>
          <h2>Step 6: Identify Assumptions</h2>
          <p className="step-description">Select or add the assumptions underlying your framework</p>
        </div>
      </div>

      <div className="step-content">
        {Object.values(wizardADerived).some(arr => arr.length > 0) && (
          <div className="derived-notice">
            <Sparkles size={18} />
            <span>Some assumptions have been auto-populated based on your Wizard A choices. You can edit them if needed.</span>
          </div>
        )}

        {dataA?.philosophy && (
          <div className="paradigm-context">
            <strong>Your Paradigm:</strong> {dataA.philosophy}
            {dataA?.philosophicalDimensions && (
              <div className="dimensions-summary">
                {dataA.philosophicalDimensions.ontology && <span><strong>Ontology:</strong> {dataA.philosophicalDimensions.ontology}</span>}
                {dataA.philosophicalDimensions.epistemology && <span><strong>Epistemology:</strong> {dataA.philosophicalDimensions.epistemology}</span>}
                {dataA.philosophicalDimensions.axiology && <span><strong>Axiology:</strong> {dataA.philosophicalDimensions.axiology}</span>}
              </div>
            )}
            {dataA?.methodologicalChoice && (
              <div className="method-summary">
                <strong>Methodology:</strong> {dataA.methodologicalChoice}
              </div>
            )}
          </div>
        )}

        <div className="assumptions-counter">
          <span className={`counter-badge ${totalSelected === 0 ? 'warning' : ''}`}>
            {totalSelected} assumptions selected
          </span>
        </div>

        <div className="assumptions-categories">
          {Object.keys(categoryLabels).map((category) => (
            <div key={category} className="assumption-category">
              <div className="category-header" style={{ borderLeftColor: categoryLabels[category].color }}>
                <h4>{categoryLabels[category].label}</h4>
                <span className="category-desc">{categoryLabels[category].desc}</span>
              </div>
              
              <div className="assumption-options">
                {predefinedAssumptions[category].map((assumption, idx) => {
                  const isSelected = (assumptions[category] || []).includes(assumption);
                  const isRecommended = recommendations?.[category]?.includes(assumption);
                  const isDerived = isDerivedFromWizardA(category, assumption);
                  
                  return (
                    <label 
                      key={idx} 
                      className={`assumption-option ${isSelected ? 'selected' : ''} ${isRecommended ? 'recommended' : ''} ${isDerived && isSelected ? 'derived' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleAssumption(category, assumption)}
                      />
                      <span className="assumption-text">{assumption}</span>
                      {isDerived && isSelected && <span className="derived-badge">From Wizard A</span>}
                      {isRecommended && !isDerived && <span className="rec-badge">Recommended</span>}
                    </label>
                  );
                })}
              </div>

              {(assumptions[category] || []).filter(a => !predefinedAssumptions[category].includes(a)).length > 0 && (
                <div className="custom-assumptions">
                  <h5>Custom Assumptions:</h5>
                  {(assumptions[category] || [])
                    .filter(a => !predefinedAssumptions[category].includes(a))
                    .map((a, idx) => (
                      <div key={idx} className="custom-assumption-item">
                        <span>{a}</span>
                        <button onClick={() => removeAssumption(category, a)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="add-custom-assumption">
          <h4>Add Custom Assumption</h4>
          <div className="custom-form">
            <select
              value={customAssumption.category}
              onChange={(e) => setCustomAssumption({ ...customAssumption, category: e.target.value })}
            >
              {Object.keys(categoryLabels).map((cat) => (
                <option key={cat} value={cat}>{categoryLabels[cat].label}</option>
              ))}
            </select>
            <input
              type="text"
              value={customAssumption.text}
              onChange={(e) => setCustomAssumption({ ...customAssumption, text: e.target.value })}
              placeholder="Enter your custom assumption..."
            />
            <button onClick={addCustomAssumption} disabled={!customAssumption.text.trim()}>
              <Plus size={18} />
              Add
            </button>
          </div>
        </div>
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={totalSelected >= 1}
        continueLabel="Continue to Propositions"
      />

      {showDerivedWarning && (
        <div className="modal-overlay">
          <div className="modal-content warning-modal">
            <div className="modal-header">
              <AlertTriangle size={24} color="#f59e0b" />
              <h3>Change Derived Assumption?</h3>
            </div>
            <div className="modal-body">
              <p>This assumption was automatically selected based on your choices in Wizard A:</p>
              <div className="derived-assumption-preview">
                <strong>{categoryLabels[showDerivedWarning.category].label}:</strong>
                <span>{showDerivedWarning.assumption}</span>
              </div>
              <p className="warning-text">
                Removing this may create inconsistencies with your earlier philosophical and methodological decisions.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDerivedWarning(null)}>
                Keep It
              </button>
              <button className="btn-warning" onClick={confirmRemoveDerived}>
                Remove Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default C4_Assumptions;
